import * as React from "react";
import cn from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/pro-light-svg-icons";
import { GetBemBlocksWith } from "@ebsco-ui/utils";

export interface Section {
  id: string;
  subSections: Section[];
  [x: string]: unknown;
}

export type OnClick = (section: Section) => void;

export interface AccordionSectionProps {
  children?: React.ReactNode;
  collapseTitle: string;
  disableAnimations?: boolean;
  expandTitle?: string;
  getBlocksWith: GetBemBlocksWith;
  isOpen: boolean;
  isSelected?: boolean;
  onClick: OnClick;
  renderCollapseTitle: (section: Section) => string;
  renderExpandTitle: (section: Section) => string;
  renderSection: (section: Section) => React.ReactNode;
  section: Section;
  showToggleButtons: boolean;
  topLevel?: boolean;
}

const AccordionSection = ({
  children,
  collapseTitle,
  disableAnimations = false,
  expandTitle,
  getBlocksWith,
  isOpen,
  isSelected = false,
  onClick,
  renderCollapseTitle,
  renderExpandTitle,
  renderSection,
  section,
  showToggleButtons,
  topLevel = false,
}: AccordionSectionProps) => {
  const toggleButtons = showToggleButtons && children;
  const handleClick = () => {
    onClick(section);
  };
  const toggleAccordionTitle = isOpen
    ? collapseTitle || renderCollapseTitle(section)
    : expandTitle || renderExpandTitle(section);

  return (
    <section
      id={`section-${section.id}`}
      className={cn(
        getBlocksWith("__section"),
        disableAnimations && getBlocksWith("__section--disable-animations"),
        isOpen && getBlocksWith("__section--open"),
        topLevel && getBlocksWith("__section--top-level"),
      )}
    >
      <div
        className={cn(
          getBlocksWith("__header"),
          isSelected && getBlocksWith("__header--selected"),
        )}
      >
        <div className={getBlocksWith("__toggle-button")}>
          {toggleButtons && (
            <button
              aria-label={toggleAccordionTitle}
              onClick={handleClick}
              aria-expanded={isOpen}
            >
              <FontAwesomeIcon
                className={cn(
                  isOpen
                    ? getBlocksWith("__toggle-button--open")
                    : getBlocksWith("__toggle-button--closed"),
                )}
                size="2x"
                icon={faAngleRight}
                title={toggleAccordionTitle}
              />
            </button>
          )}
        </div>
        <section className={cn(getBlocksWith("__title"))}>
          {renderSection(section)}
        </section>
      </div>
      <div
        hidden={!isOpen}
        className={cn(
          getBlocksWith("__children"),
          isOpen && getBlocksWith("__children--open"),
        )}
      >
        {children}
      </div>
    </section>
  );
};

export default AccordionSection;
